import { useState } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { apiClient, ApiError } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'
import { AuthShell } from './SignInPage'

const Schema = z.object({
  password: z.string().min(12).max(128),
})
type FormValues = z.infer<typeof Schema>

export function ResetPasswordPage() {
  const t = useT()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [serverError, setServerError] = useState<string | null>(null)
  const token = params.get('token') ?? ''
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(Schema),
  })

  const reset = useMutation({
    mutationFn: (values: FormValues) => apiClient.post('/auth/reset-password', { token, password: values.password }),
    onSuccess: () => navigate('/auth/signin?reset=ok'),
    onError: (err) => setServerError(err instanceof ApiError ? err.message : 'Reset failed'),
  })

  if (!token) {
    return (
      <AuthShell>
        <h1 className="text-2xl font-semibold mb-1">{t('auth.reset.title')}</h1>
        <p className="text-sm" style={{ color: 'var(--color-critical)' }}>{t('auth.reset.missing_token')}</p>
        <Link to="/auth/forgot" className="text-sm underline mt-4 inline-block">{t('auth.forgot.link')}</Link>
      </AuthShell>
    )
  }

  return (
    <AuthShell>
      <h1 className="text-2xl font-semibold mb-1">{t('auth.reset.title')}</h1>
      <p className="text-sm opacity-70 mb-6">{t('auth.reset.subtitle')}</p>

      <form onSubmit={handleSubmit((v) => reset.mutate(v))} className="flex flex-col gap-3">
        <label className="block">
          <span className="text-sm opacity-80">{t('auth.password.new')}</span>
          <input
            {...register('password')}
            type="password"
            autoFocus
            autoComplete="new-password"
            className="auth-input mt-1"
          />
          {errors.password && <p className="text-xs mt-1" style={{ color: 'var(--color-critical)' }}>{errors.password.message}</p>}
        </label>

        {serverError && <p className="text-sm" style={{ color: 'var(--color-critical)' }}>{serverError}</p>}

        <button
          type="submit"
          disabled={reset.isPending}
          className="mt-1 px-3 py-2 rounded font-medium text-sm"
          style={{ background: 'var(--color-accent)', color: 'white' }}
        >
          {reset.isPending ? t('common.loading') : t('auth.reset.cta')}
        </button>
      </form>
    </AuthShell>
  )
}
